// src/modules/components/Header/styles.js
// VEGA ROBOTICS - Header Styles (Paleta Vega + TopBar + Progress Bar + Skeletons)
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

// ============================================================
// DESIGN TOKENS - PALETA VEGA
// ============================================================
export const colors = {
  // Backgrounds
  bgWhite: '#FFFFFF',
  bgLight: '#F5F5F5',
  bgDark: '#111111',
  
  // Primary brand (botões - NÃO usar vermelho!)
  primary: '#111111',
  primaryDark: '#2A2A2A',
  
  // Accent (hovers, acentos)
  vegaRed: '#9D0008',
  
  // Text
  textDark: '#111111',
  textBody: '#333333',
  textMuted: '#666666',
  textWhite: '#FFFFFF',
  textWhiteMuted: 'rgba(255, 255, 255, 0.6)',
  
  // Borders
  border: 'rgba(0, 0, 0, 0.08)',
  borderMedium: 'rgba(0, 0, 0, 0.12)',
  borderDark: 'rgba(255, 255, 255, 0.1)',
  
  // Skeleton
  skeletonBase: '#E0E0E0',
  skeletonShine: '#F5F5F5',
};

// ============================================================
// PROGRESS BAR (YouTube Style)
// ============================================================
const progressAnimation = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const progressPulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

export const ProgressBarContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 1002;
  overflow: hidden;
  pointer-events: none;
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  background: ${colors.vegaRed};
  width: ${props => props.$progress}%;
  transition: width ${props => props.$isFinishing ? '200ms' : '400ms'} ease;
  box-shadow: 0 0 10px ${colors.vegaRed}, 0 0 5px ${colors.vegaRed};
  
  /* Efeito de brilho na ponta */
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 100px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(255, 255, 255, 0.4)
    );
    animation: ${progressPulse} 1s ease-in-out infinite;
    opacity: ${props => props.$progress > 0 && props.$progress < 100 ? 1 : 0};
  }
`;

export const ProgressBarIndeterminate = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 30%;
  background: ${colors.vegaRed};
  box-shadow: 0 0 10px ${colors.vegaRed};
  animation: indeterminate 1.5s ease-in-out infinite;
  
  @keyframes indeterminate {
    0% {
      left: -30%;
    }
    100% {
      left: 100%;
    }
  }
`;

// ============================================================
// SKELETON COMPONENTS
// ============================================================
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const SkeletonBase = styled.div`
  background: ${colors.skeletonBase};
  background-image: linear-gradient(
    90deg,
    ${colors.skeletonBase} 0px,
    ${colors.skeletonShine} 40px,
    ${colors.skeletonBase} 80px
  );
  background-size: 600px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

export const SkeletonText = styled(SkeletonBase)`
  height: ${props => props.$height || '16px'};
  width: ${props => props.$width || '100%'};
  margin-bottom: ${props => props.$mb || '8px'};
  border-radius: ${props => props.$rounded ? '9999px' : '4px'};
`;

export const SkeletonTitle = styled(SkeletonBase)`
  height: ${props => props.$height || '24px'};
  width: ${props => props.$width || '60%'};
  margin-bottom: ${props => props.$mb || '16px'};
`;

export const SkeletonImage = styled(SkeletonBase)`
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '200px'};
  border-radius: ${props => props.$radius || '8px'};
`;

export const SkeletonCircle = styled(SkeletonBase)`
  width: ${props => props.$size || '48px'};
  height: ${props => props.$size || '48px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

export const SkeletonCard = styled.div`
  background: ${colors.bgWhite};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;

export const SkeletonButton = styled(SkeletonBase)`
  height: ${props => props.$height || '44px'};
  width: ${props => props.$width || '120px'};
  border-radius: ${props => props.$radius || '4px'};
`;

// ============================================================
// TOP BAR (Faixa preta superior)
// ============================================================
export const TopBar = styled.div`
  background: ${colors.bgDark};
  border-bottom: 1px solid ${colors.borderDark};
`;

export const TopBarInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 48px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1024px) {
    padding: 0 24px;
    height: 32px;
  }

  @media (max-width: 640px) {
    padding: 0 16px;
  }
`;

export const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

export const TopBarLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const TopBarLink = styled.a`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.textWhiteMuted};
  text-decoration: none;
  letter-spacing: 0.02em;
  transition: color 200ms ease;
  min-height: 44px;
  display: flex;
  align-items: center;

  &:hover {
    color: ${colors.textWhite};
  }
`;

// ============================================================
// FLAG ICON - Bandeiras SVG
// ============================================================
export const FlagIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

// ============================================================
// LANGUAGE SELECTOR
// ============================================================
export const LanguageSelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: ${colors.textWhiteMuted};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 200ms ease;
  
  &:hover {
    color: ${colors.textWhite};
    background: rgba(255, 255, 255, 0.05);
    border-color: ${colors.borderDark};
  }
  
  .chevron {
    font-size: 10px;
    transition: transform 200ms ease;
    opacity: 0.7;
  }
  
  &.open {
    background: rgba(255, 255, 255, 0.08);
    border-color: ${colors.borderDark};
    
    .chevron {
      transform: rotate(180deg);
    }
  }
`;

export const LanguageDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 160px;
  background: ${colors.bgWhite};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 6px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-8px'});
  transition: all 200ms ease;
  z-index: 1001;
`;

export const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.$active ? colors.bgLight : 'transparent'};
  border: none;
  border-radius: 6px;
  color: ${colors.textBody};
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '400'};
  text-align: left;
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background: ${colors.bgLight};
  }
  
  .check {
    margin-left: auto;
    font-size: 12px;
    color: ${colors.vegaRed};
    opacity: ${props => props.$active ? 1 : 0};
    transition: opacity 150ms ease;
  }
`;

// ============================================================
// HEADER CONTAINER
// ============================================================
export const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
`;

export const HeaderContainer = styled.header`
  position: relative;
  background: ${colors.bgWhite};
  border-bottom: 1px solid ${props => props.$isScrolled ? colors.borderMedium : colors.border};
  box-shadow: ${props => props.$isScrolled ? '0 2px 20px rgba(0,0,0,0.06)' : 'none'};
  transition: all 300ms ease;
`;

export const HeaderInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  
  @media (max-width: 1024px) {
    padding: 0 24px;
    height: 64px;
  }
  
  @media (max-width: 640px) {
    padding: 0 16px;
    height: 60px;
  }
`;

// ============================================================
// LOGO
// ============================================================
export const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  
  img {
    height: 38px;
    transition: opacity 300ms ease;
    
    @media (max-width: 640px) {
      height: 32px;
    }
  }
  
  &:hover img {
    opacity: 0.7;
  }
`;

// ============================================================
// NAVIGATION
// ============================================================
export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

export const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const NavItem = styled.div`
  position: relative;
`;

// Link com React Router (para rotas)
export const NavLink = styled(Link)`
  padding: 12px 16px;
  color: ${colors.textBody};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-decoration: none;
  transition: color 200ms ease;
  
  &:hover {
    color: ${colors.vegaRed};
  }
`;

// Link para scroll (âncoras)
export const NavLinkScroll = styled.a`
  padding: 12px 16px;
  color: ${colors.textBody};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-decoration: none;
  cursor: pointer;
  transition: color 200ms ease;
  
  &:hover {
    color: ${colors.vegaRed};
  }
`;

export const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: ${colors.textBody};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: color 200ms ease;
  
  &:hover {
    color: ${colors.vegaRed};
  }
  
  svg {
    font-size: 10px;
    transition: transform 200ms ease;
  }
  
  &.open svg {
    transform: rotate(180deg);
  }
`;

// ============================================================
// DROPDOWN (mantido para uso futuro)
// ============================================================
export const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 220px;
  background: ${colors.bgWhite};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateX(-50%) translateY(${props => props.$isOpen ? '0' : '-10px'});
  transition: all 200ms ease;
`;

export const DropdownLink = styled(Link)`
  display: block;
  padding: 12px 16px;
  color: ${colors.textBody};
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 6px;
  transition: all 200ms ease;
  
  &:hover {
    background: ${colors.bgLight};
    color: ${colors.vegaRed};
  }
`;

export const DropdownExternalLink = styled.a`
  display: block;
  padding: 12px 16px;
  color: ${colors.textBody};
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 6px;
  transition: all 200ms ease;
  
  &:hover {
    background: ${colors.bgLight};
    color: ${colors.vegaRed};
  }
`;

// ============================================================
// CONTACT BUTTON
// ============================================================
export const ContactButton = styled.button`
  padding: 10px 24px;
  margin-left: 16px;
  background: ${colors.primary};
  color: ${colors.textWhite};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 200ms ease;

  &:hover {
    background: ${colors.primaryDark};
    transform: translateY(-1px);
  }
`;

// ============================================================
// MOBILE
// ============================================================
export const MobileToggle = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  color: ${colors.textDark};
  cursor: pointer;
  
  svg {
    font-size: 22px;
  }
  
  @media (max-width: 1024px) {
    display: flex;
  }
`;

export const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.bgWhite};
  padding: 120px 24px 24px;
  z-index: 999;
  overflow-y: auto;
  
  @media (max-width: 1024px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

export const MobileNavLink = styled(Link)`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.textDark};
  text-decoration: none;
  border-bottom: 1px solid ${colors.border};

  &:hover, &:active {
    color: ${colors.vegaRed};
  }

  @media (max-width: 480px) {
    font-size: 16px;
    min-height: 44px;
  }
`;

// Link de scroll para mobile
export const MobileNavLinkScroll = styled.a`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.textDark};
  text-decoration: none;
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;

  &:hover, &:active {
    color: ${colors.vegaRed};
  }

  @media (max-width: 480px) {
    font-size: 16px;
    min-height: 44px;
  }
`;

export const MobileExternalLink = styled.a`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.textDark};
  text-decoration: none;
  border-bottom: 1px solid ${colors.border};

  &:hover, &:active {
    color: ${colors.vegaRed};
  }

  @media (max-width: 480px) {
    font-size: 16px;
    min-height: 44px;
  }
`;

export const MobileContactButton = styled.button`
  display: block;
  width: 100%;
  margin-top: 24px;
  padding: 16px 24px;
  background: ${colors.primary};
  color: ${colors.textWhite};
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  border-radius: 4px;
  border: none;
  cursor: pointer;

  &:hover {
    background: ${colors.primaryDark};
  }
`;

// ============================================================
// MOBILE LANGUAGE SELECTOR - Com Bandeiras
// ============================================================
export const MobileLanguageSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${colors.border};
`;

export const MobileLanguageOption = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: ${props => props.$active ? colors.bgDark : colors.bgLight};
  color: ${props => props.$active ? colors.textWhite : colors.textBody};
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms ease;

  &:hover {
    background: ${props => props.$active ? colors.bgDark : colors.borderMedium};
  }

  img {
    width: 22px;
    height: 22px;
  }
`;

// ============================================================
// LOGIN BUTTON (Admin Access)
// ============================================================
export const LoginButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: ${colors.textWhite};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms ease;

  svg {
    font-size: 14px;
    color: #4285f4;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

export const UserMenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const UserAvatar = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 20px;
  color: ${colors.textWhiteMuted};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms ease;

  img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
  }

  .chevron {
    font-size: 10px;
    transition: transform 200ms ease;
    opacity: 0.7;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${colors.borderDark};
    color: ${colors.textWhite};
  }

  &.open {
    background: rgba(255, 255, 255, 0.08);
    border-color: ${colors.borderDark};

    .chevron {
      transform: rotate(180deg);
    }
  }
`;

export const UserDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: ${colors.bgWhite};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-8px'});
  transition: all 200ms ease;
  z-index: 1001;
`;

export const UserInfo = styled.div`
  padding: 12px;
  border-bottom: 1px solid ${colors.border};
  margin-bottom: 8px;

  .name {
    font-size: 14px;
    font-weight: 600;
    color: ${colors.textDark};
    margin-bottom: 2px;
  }

  .email {
    font-size: 12px;
    color: ${colors.textMuted};
  }
`;

export const UserMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.textBody};
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: all 150ms ease;

  svg {
    font-size: 14px;
    color: ${colors.textMuted};
  }

  &:hover {
    background: ${colors.bgLight};
  }

  &.danger {
    color: ${colors.vegaRed};

    svg {
      color: ${colors.vegaRed};
    }
  }
`;

export const UserMenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.textBody};
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: all 150ms ease;

  svg {
    font-size: 14px;
    color: ${colors.textMuted};
  }

  &:hover {
    background: ${colors.bgLight};
  }
`;
